import { useState, useMemo } from "react";

// Constantes normativas 2026 (Perú)
const RMV_2026 = 1130;
const ASIGNACION_FAMILIAR = RMV_2026 * 0.10;

const PERIODOS = [
  {
    id: "mayo",
    etiqueta: "Mayo (periodo Nov – Abr)",
    mesesTope: 6,
    diasTope: 180,
  },
  {
    id: "noviembre",
    etiqueta: "Noviembre (periodo May – Oct)",
    mesesTope: 6,
    diasTope: 180,
  },
];

const REGIMENES = [
  { id: "general", nombre: "Régimen General", detalle: "30 días/año", factor: 1, sinCTS: false },
  { id: "pequena", nombre: "Pequeña Empresa", detalle: "15 días/año", factor: 0.5, sinCTS: false },
  { id: "micro", nombre: "Microempresa", detalle: "Sin CTS", factor: 0, sinCTS: true },
];

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function diasEntreFechas(fechaInicioStr, fechaFinStr) {
  if (!fechaInicioStr || !fechaFinStr) return 0;
  const [y1, m1, d1] = fechaInicioStr.split("-").map(Number);
  const [y2, m2, d2] = fechaFinStr.split("-").map(Number);
  if (!y1 || !y2) return 0;
  const dias = (y2 - y1) * 360 + (m2 - m1) * 30 + (d2 - d1) + 1;
  return Math.max(0, dias);
}
function evitarEnter(e) {
  if (e.key === "Enter") {
    e.preventDefault();
    e.currentTarget.blur();
  }
}

export default function CalculadoraCTS() {
  const [regimenId, setRegimenId] = useState("general");
  const [periodoId, setPeriodoId] = useState("mayo");
  const [sueldoBruto, setSueldoBruto] = useState(3000);
  const [tieneHijos, setTieneHijos] = useState(false);
  const [incluirSexto, setIncluirSexto] = useState(true);

  const [modoTiempo, setModoTiempo] = useState("meses"); 
  const [mesesCompletos, setMesesCompletos] = useState(6);
  const [diasSueltos, setDiasSueltos] = useState(0);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [diasInasistencia, setDiasInasistencia] = useState(0);

  const [comisiones, setComisiones] = useState(0);
  const [horasExtras, setHorasExtras] = useState(0);
  const [alimentacion, setAlimentacion] = useState(0);
  const [bonificaciones, setBonificaciones] = useState(0);

  const periodo = PERIODOS.find((p) => p.id === periodoId) ?? PERIODOS[0];
  const regimen = REGIMENES.find((r) => r.id === regimenId) ?? REGIMENES[0];

  const resultado = useMemo(() => {
    const bruto = Number(sueldoBruto) || 0;
    const asignacion = tieneHijos ? ASIGNACION_FAMILIAR : 0;

    const ingresosAdicionales =
      (Number(comisiones) || 0) +
      (Number(horasExtras) || 0) +
      (Number(alimentacion) || 0) +
      (Number(bonificaciones) || 0);

    const remuneracionComputable = bruto + asignacion + ingresosAdicionales;

    const sextoGratificacion = incluirSexto ? remuneracionComputable / 6 : 0;
    const baseComputable = remuneracionComputable + sextoGratificacion;

    const inasistencias = Math.max(0, Number(diasInasistencia) || 0);

    let diasBrutos = 0;
    if (modoTiempo === "meses") {
      const meses = Math.min(6, Math.max(0, Number(mesesCompletos) || 0));
      const dias = Math.max(0, Number(diasSueltos) || 0);
      diasBrutos = meses * 30 + dias;
    } else {
      diasBrutos = diasEntreFechas(fechaInicio, fechaFin);
    }

    const diasComputables = Math.max(0, Math.min(180, diasBrutos - inasistencias));

    const ctsBase = baseComputable * (diasComputables / 360);
    const cts = regimen.sinCTS ? 0 : ctsBase * regimen.factor;

    return {
      bruto,
      asignacion,
      ingresosAdicionales,
      remuneracionComputable,
      sextoGratificacion,
      baseComputable,
      diasBrutos,
      diasComputables,
      inasistencias,
      cts,
    };
  }, [
    sueldoBruto,
    tieneHijos,
    incluirSexto,
    modoTiempo,
    mesesCompletos,
    diasSueltos,
    fechaInicio,
    fechaFin,
    diasInasistencia,
    comisiones,
    horasExtras,
    alimentacion,
    bonificaciones,
    regimen,
  ]);

  const bloqueado = regimen.sinCTS;

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            🏦
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de CTS
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Compensación por Tiempo de Servicios</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Régimen laboral
              </label>
              <div className="grid grid-cols-3 gap-2">
                {REGIMENES.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRegimenId(r.id)}
                    className={`rounded-lg border py-2.5 px-1 text-center transition-colors ${
                      regimenId === r.id
                        ? "border-red-700 bg-red-50 text-red-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <span className="block text-xs font-semibold leading-tight">{r.nombre}</span>
                    <span className="block mt-0.5 text-[11px] text-slate-400">{r.detalle}</span>
                  </button>
                ))}
              </div>
            </div>

            {bloqueado && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3.5">
                <span className="text-amber-600">⚠️</span>
                <p className="text-xs leading-relaxed text-amber-800">
                  Los trabajadores del régimen de <strong>Microempresa</strong> (Ley N.° 30056) no
                  tienen derecho a CTS por ley. El resto del formulario queda deshabilitado.
                </p>
              </div>
            )}

            <div
              className={`space-y-5 transition-opacity ${
                bloqueado ? "opacity-40 pointer-events-none select-none" : ""
              }`}
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Periodo de depósito
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PERIODOS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPeriodoId(p.id)}
                      className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${
                        periodoId === p.id
                          ? "border-red-700 bg-red-50 text-red-800"
                          : "border-slate-300 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {p.id === "mayo" ? "Mayo" : "Noviembre"}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-slate-400">{periodo.etiqueta}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Sueldo bruto mensual
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                    S/
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={sueldoBruto}
                    onChange={(e) => setSueldoBruto(e.target.value)}
                    onKeyDown={evitarEnter}
                    className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-4 py-2.5 text-slate-900 font-mono text-lg focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                    placeholder="3000"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={tieneHijos}
                  onChange={(e) => setTieneHijos(e.target.checked)}
                  className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
                />
                <span className="text-sm text-slate-700">
                  Recibo asignación familiar ({formatearSoles(ASIGNACION_FAMILIAR)})
                </span>
              </label>

              <div>
                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={incluirSexto}
                    onChange={(e) => setIncluirSexto(e.target.checked)}
                    className="w-4 h-4 mt-0.5 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-700">
                      Incluir 1/6 de gratificación (recomendado)
                    </span>
                    <span className="block mt-0.5 text-xs text-slate-400">
                      Por ley, 1/6 de tu gratificación se suma a la base de cálculo.
                    </span>
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Tiempo trabajado
                </label>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setModoTiempo("meses")}
                    className={`rounded-lg border py-2 text-sm font-medium transition-colors ${
                      modoTiempo === "meses"
                        ? "border-red-700 bg-red-50 text-red-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    Por meses
                  </button>
                  <button
                    type="button"
                    onClick={() => setModoTiempo("fechas")}
                    className={`rounded-lg border py-2 text-sm font-medium transition-colors ${
                      modoTiempo === "fechas"
                        ? "border-red-700 bg-red-50 text-red-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    Por fechas exactas
                  </button>
                </div>

                {modoTiempo === "meses" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Meses completos trabajados
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="6"
                        value={mesesCompletos}
                        onChange={(e) => setMesesCompletos(e.target.value)}
                        onKeyDown={evitarEnter}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      />
                      <p className="mt-1 text-xs text-slate-400">de 0 a 6 (semestre completo)</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Días sueltos
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="29"
                        value={diasSueltos}
                        onChange={(e) => setDiasSueltos(e.target.value)}
                        onKeyDown={evitarEnter}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      />
                      <p className="mt-1 text-xs text-slate-400">del mes incompleto</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Fecha de inicio
                      </label>
                      <input
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Fecha de fin
                      </label>
                      <input
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-400">
                      Se calcula con el método legal de meses de 30 días (año de 360 días), con
                      tope de 180 días por semestre.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Días de inasistencia injustificada
                </label>
                <input
                  type="number"
                  min="0"
                  value={diasInasistencia}
                  onChange={(e) => setDiasInasistencia(e.target.value)}
                  onKeyDown={evitarEnter}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-slate-400">
                  Se descuentan del tiempo computable (no aplica a licencias con goce ni descanso médico)
                </p>
              </div>

              <div className="pt-1 border-t border-slate-100">
                <p className="mt-4 text-sm font-medium text-slate-700">
                  Ingresos adicionales (opcional)
                </p>
                <p className="mt-1 mb-3 text-xs leading-relaxed text-slate-400">
                  Solo incluye conceptos que recibes de manera regular (al menos 3 meses
                  consecutivos). Estos montos se suman a tu remuneración computable.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Comisiones (S/)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={comisiones}
                      onChange={(e) => setComisiones(e.target.value)}
                      onKeyDown={evitarEnter}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Horas Extras (S/)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={horasExtras}
                      onChange={(e) => setHorasExtras(e.target.value)}
                      onKeyDown={evitarEnter}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Alimentación (S/)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={alimentacion}
                      onChange={(e) => setAlimentacion(e.target.value)}
                      onKeyDown={evitarEnter}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Bonificaciones (S/)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={bonificaciones}
                      onChange={(e) => setBonificaciones(e.target.value)}
                      onKeyDown={evitarEnter}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              CTS estimada a depositar
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.cts))}
            </p>

            {bloqueado ? (
              <p className="text-sm text-slate-400">
                Régimen Microempresa: no corresponde depósito de CTS por ley.
              </p>
            ) : (
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Remuneración computable</span>
                  <span className="font-mono">{formatearSoles(resultado.remuneracionComputable)}</span>
                </div>
                {resultado.ingresosAdicionales > 0 && (
                  <div className="flex justify-between text-slate-400">
                    <span>&nbsp;&nbsp;incluye ingresos adicionales</span>
                    <span className="font-mono">{formatearSoles(resultado.ingresosAdicionales)}</span>
                  </div>
                )}
                {incluirSexto && (
                  <div className="flex justify-between text-slate-300">
                    <span>+ 1/6 de gratificación</span>
                    <span className="font-mono">{formatearSoles(resultado.sextoGratificacion)}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-300 border-t border-slate-700 pt-1.5 mt-1.5">
                  <span>Base computable</span>
                  <span className="font-mono">{formatearSoles(resultado.baseComputable)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Días computables</span>
                  <span className="font-mono">
                    {resultado.diasComputables} / 180
                    {resultado.inasistencias > 0 && ` (−${resultado.inasistencias} inasist.)`}
                  </span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Régimen</span>
                  <span className="font-mono">{regimen.nombre}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Herramienta orientativa. Fórmula general: (Remuneración computable + 1/6 de
          gratificación) ÷ 12 meses × tiempo computable, ajustada según el régimen laboral
          (General: 30 días/año, Pequeña empresa: 15 días/año, Microempresa: sin CTS). No
          incluye conceptos remunerativos adicionales fuera de los declarados. El empleador
          tiene hasta el día 15 del mes de depósito para realizarlo. No constituye asesoría
          legal, contable ni laboral.
        </p>
      </div>
    </div>
  );
}